import { useEffect, useRef } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/blog`;

function send(payload) {
  const body = JSON.stringify(payload);
  if (navigator.sendBeacon) {
    navigator.sendBeacon(API, new Blob([body], { type: "application/json" }));
  } else {
    fetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  }
}

export function useBlogAnalytics(slug) {
  const sentDepths = useRef(new Set());
  const startedAt = useRef(Date.now());

  useEffect(() => {
    startedAt.current = Date.now();
    sentDepths.current = new Set();
    send({ slug, event: "view" });

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = ((window.scrollY / scrollable) * 100) | 0;
      for (const m of [25, 50, 75, 100]) {
        if (pct >= m && !sentDepths.current.has(m)) {
          sentDepths.current.add(m);
          send({ slug, event: "scroll", value: m });
        }
      }
    };

    const flushTime = () => {
      const secs = Math.round((Date.now() - startedAt.current) / 1000);
      if (secs >= 3) send({ slug, event: "time", value: secs });
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") flushTime();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onHide);
      flushTime();
    };
  }, [slug]);
}

export function setPageMeta({ title, description, image, url }) {
  document.title = title;
  const ensure = (attr, key, content) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };
  ensure("name", "description", description);
  ensure("property", "og:title", title);
  ensure("property", "og:description", description);
  ensure("property", "og:type", "article");
  ensure("property", "og:url", url);
  ensure("property", "og:image", image);
  ensure("name", "twitter:card", "summary_large_image");
  ensure("name", "twitter:title", title);
  ensure("name", "twitter:description", description);
  ensure("name", "twitter:image", image);
}
